import type { VehiclePricingCategory } from './parkingPricing';

export const DEFAULT_VEHICLE_TYPE: VehiclePricingCategory = 'passenger_cars';

const VEHICLE_TYPE_LABELS: Record<VehiclePricingCategory, string> = {
  passenger_cars: 'Samochód osobowy',
  trucks: 'Ciężarówka',
  buses: 'Autobus',
  moto_bikes: 'Motocykl',
  campers: 'Kamper',
  charging_station: 'Pojazd ładujący',
};

export const VEHICLE_TYPE_OPTIONS: Array<{ value: VehiclePricingCategory; label: string }> = [
  { value: 'passenger_cars', label: VEHICLE_TYPE_LABELS.passenger_cars },
  { value: 'trucks', label: VEHICLE_TYPE_LABELS.trucks },
  { value: 'buses', label: VEHICLE_TYPE_LABELS.buses },
  { value: 'moto_bikes', label: VEHICLE_TYPE_LABELS.moto_bikes },
  { value: 'campers', label: VEHICLE_TYPE_LABELS.campers },
];

export type StoredVehicle = {
  id: string;
  licensePlate: string;
  brand: string;
  model: string;
  vehicleType: VehiclePricingCategory;
  createdAt: string;
};

const VEHICLES_STORAGE_KEY = 'parking-box:vehicles';

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

export const normalizeVehicleLicensePlate = (value: string): string => value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

const normalizeVehicleType = (value: unknown): VehiclePricingCategory => {
  if (typeof value !== 'string') {
    return DEFAULT_VEHICLE_TYPE;
  }

  const normalized = value.trim();

  switch (normalized) {
    case 'passenger_cars':
    case 'trucks':
    case 'buses':
    case 'moto_bikes':
    case 'campers':
    case 'charging_station':
      return normalized as VehiclePricingCategory;
    case 'kampery':
      return 'campers';
    default:
      return DEFAULT_VEHICLE_TYPE;
  }
};

export const getVehicleTypeLabel = (vehicleType: VehiclePricingCategory): string =>
  VEHICLE_TYPE_LABELS[vehicleType] ?? VEHICLE_TYPE_LABELS.passenger_cars;

const normalizeStoredVehicle = (value: unknown): StoredVehicle | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const id = typeof record.id === 'string' && record.id.trim().length > 0 ? record.id.trim() : null;
  const licensePlate =
    typeof record.licensePlate === 'string' && record.licensePlate.trim().length > 0 ? record.licensePlate.trim().toUpperCase() : null;
  const brand = typeof record.brand === 'string' && record.brand.trim().length > 0 ? record.brand.trim() : null;
  const model = typeof record.model === 'string' && record.model.trim().length > 0 ? record.model.trim() : null;
  const vehicleType = normalizeVehicleType(record.vehicleType ?? record.vehicle_type ?? record.type);
  const createdAt = typeof record.createdAt === 'string' && record.createdAt.trim().length > 0 ? record.createdAt.trim() : null;

  if (!id || !licensePlate || !brand || !model || !createdAt) {
    return null;
  }

  return {
    id,
    licensePlate,
    brand,
    model,
    vehicleType,
    createdAt,
  };
};

export const readStoredVehicles = (): StoredVehicle[] => {
  if (!canUseStorage()) {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(VEHICLES_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map((entry) => normalizeStoredVehicle(entry)).filter((entry): entry is StoredVehicle => Boolean(entry));
  } catch {
    return [];
  }
};

const writeStoredVehicles = (vehicles: StoredVehicle[]) => {
  if (!canUseStorage()) {
    return;
  }

  try {
    window.localStorage.setItem(VEHICLES_STORAGE_KEY, JSON.stringify(vehicles));
  } catch {
    // Ignore storage write failures.
  }
};

export const saveVehicle = (vehicle: Omit<StoredVehicle, 'id' | 'createdAt'>): StoredVehicle => {
  const nextVehicle: StoredVehicle = {
    id: `vehicle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    licensePlate: normalizeVehicleLicensePlate(vehicle.licensePlate),
    brand: vehicle.brand.trim(),
    model: vehicle.model.trim(),
    vehicleType: normalizeVehicleType(vehicle.vehicleType),
    createdAt: new Date().toISOString(),
  };

  const currentVehicles = readStoredVehicles();
  const withoutSamePlate = currentVehicles.filter((entry) => entry.licensePlate !== nextVehicle.licensePlate);
  const nextVehicles = [nextVehicle, ...withoutSamePlate];

  writeStoredVehicles(nextVehicles);
  return nextVehicle;
};

export const deleteVehicle = (licensePlate: string): StoredVehicle[] => {
  const normalizedPlate = normalizeVehicleLicensePlate(licensePlate);
  if (!normalizedPlate) {
    return readStoredVehicles();
  }

  const currentVehicles = readStoredVehicles();
  const nextVehicles = currentVehicles.filter((entry) => entry.licensePlate !== normalizedPlate);

  writeStoredVehicles(nextVehicles);
  return nextVehicles;
};
